import { useMemo, useState } from 'react'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { Eye, FileDown, Pencil, Plus, Printer, Rows3 } from 'lucide-react'

import type {
    DriedTeaPurchase,
    ListDriedTeaPurchasesParams,
} from '@/lib/api/dried-tea-purchase-api'
import { PageBackButton } from '@/components/common/PageBackButton'
import { ClearFilterButton } from '@/components/common/clear-filter-button'
import { defineListFilters } from '@/components/common/filters/core'
import { dateFilter } from '@/components/common/filters/date-filter'
import { growerFilter } from '@/components/common/filters/grower-filter'
import { useListFilters } from '@/components/common/filters/use-list-filters'
import { ListFilterBar } from '@/components/common/ListFilterBar'
import { LIST_CARD_TITLE_CLASS, ListCard } from '@/components/common/ListCard'
import { RowDeleteButton } from '@/components/common/row-delete-button'
import { Tooltip } from '@/components/ui/tooltip'
import { BallotCell } from '@/components/dried-tea-purchases/ballot-cell'
import { DriedFreshSlipsPrintDialog } from '@/components/dried-tea-purchases/dried-fresh-slips-print-dialog'
import { DriedTeaPurchaseBulkDialog } from '@/components/dried-tea-purchases/dried-tea-purchase-bulk-dialog'
import { DriedTeaPurchaseFormDialog } from '@/components/dried-tea-purchases/dried-tea-purchase-form-dialog'
import { DriedTeaPurchasePrintDialog } from '@/components/dried-tea-purchases/dried-tea-purchase-print-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { useAuth } from '@/hooks/use-auth'
import { useGrower } from '@/hooks/use-growers-query'
import {
    useDeleteDriedTeaPurchase,
    useDriedTeaPurchasesList,
    useExportDriedTeaPurchases,
} from '@/hooks/use-dried-tea-purchase-query'
import { formatQty, formatVN } from '@/lib/format'
import { gradeLabel } from '@/lib/tea-purchase-labels'
import { driedTeaPurchaseDeleteBlockReason } from '@/lib/tea-purchase-delete-guard'
import { m } from '@/lib/paraglide/messages'

/**
 * Bộ lọc khai một lần, suy ra: parser URL, mặc định, badge đếm, chip.
 *
 * Bộ lọc theo id hộ trồng không suy ra được nhãn chip từ giá trị — màn tra
 * tên rồi truyền qua `chipLabels`. Phiếu khô không còn loại chè (M-27) nên
 * không lọc theo loại chè.
 */
const FILTERS = defineListFilters({
    grower: growerFilter({ label: m.tea_purchase_filter_grower }),
    date_from: dateFilter({ label: m.common_date_from }),
    date_to: dateFilter({ label: m.common_date_to }),
})

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/dried-tea-purchases/',
)({
    component: DriedTeaPurchaseListPage,
    validateSearch: FILTERS.validateSearch,
})

function DriedTeaPurchaseListPage() {
    const { hasPermission } = useAuth()
    const canWrite = hasPermission('tea_purchases.write')

    const navigate = useNavigate({ from: Route.fullPath })
    const urlSearch = Route.useSearch()

    // Lọc theo id: chip cần TÊN, URL chỉ có id → tra runtime rồi truyền xuống.
    const growerId =
        typeof urlSearch.grower === 'string' ? urlSearch.grower : undefined
    const { data: selectedGrower } = useGrower(growerId)

    const { values, barProps, page, setPage } = useListFilters({
        def: FILTERS,
        search: urlSearch,
        onSearchChange: (next, opts) =>
            void navigate({
                search: () => next,
                replace: opts?.replace ?? false,
            }),
        columns: 3,
        chipLabels: {
            grower: selectedGrower
                ? `${m.tea_purchase_filter_grower()}: ${selectedGrower.name}`
                : undefined,
        },
    })

    const [formState, setFormState] = useState<{
        open: boolean
        dried?: DriedTeaPurchase | null
    }>({ open: false })
    const [bulkOpen, setBulkOpen] = useState(false)
    const [printTarget, setPrintTarget] = useState<DriedTeaPurchase | null>(
        null,
    )
    const [freshPrintTarget, setFreshPrintTarget] =
        useState<DriedTeaPurchase | null>(null)
    const [multiPrintOpen, setMultiPrintOpen] = useState(false)
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
    const toggleId = (id: string) =>
        setSelectedIds((s) => {
            const next = new Set(s)
            if (next.has(id)) next.delete(id)
            else next.add(id)
            return next
        })

    // Map bộ lọc → tham số API. Để tường minh ở màn: mỗi màn map một kiểu.
    const params = useMemo<ListDriedTeaPurchasesParams>(
        () => ({
            page,
            per_page: 50,
            ...(values.grower ? { grower_id: values.grower } : {}),
            ...(values.date_from ? { date_from: values.date_from } : {}),
            ...(values.date_to ? { date_to: values.date_to } : {}),
        }),
        [page, values.grower, values.date_from, values.date_to],
    )
    const { data, isLoading, isFetching, refetch } =
        useDriedTeaPurchasesList(params)
    const exportExcel = useExportDriedTeaPurchases()
    const items = data?.data ?? []
    const total = data?.meta?.total ?? 0
    // Giống "In nhiều phiếu" bên cân tươi: chỉ in phiếu đã tick ở trang đang xem.
    const selectedRows = items.filter((n) => selectedIds.has(n.id))

    // Rỗng vì chưa có dữ liệu khác hẳn rỗng vì bộ lọc quá hẹp — trường hợp thứ
    // hai phải cho người dùng lối thoát ngay tại chỗ (R10).
    const emptyState = (
        <div className="flex flex-col items-center gap-3 py-8 text-center">
            <span className="text-muted-foreground">
                {barProps.activeCount > 0
                    ? m.list_empty_filtered()
                    : m.common_no_data()}
            </span>
            {barProps.activeCount > 0 ? (
                <ClearFilterButton active onClear={barProps.onClear} />
            ) : null}
        </div>
    )

    return (
        <div className="space-y-4 page-pad">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex min-w-0 items-start gap-2">
                    <PageBackButton className="-my-2 md:-my-1" />
                    <div>
                        <h1 className="text-lg md:text-2xl font-semibold">
                            {m.dtp_title()}
                        </h1>
                        <p className="text-sm text-muted-foreground">
                            {m.dtp_list_desc()}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        size="sm"
                        variant="outline"
                        disabled={selectedRows.length === 0}
                        onClick={() => setMultiPrintOpen(true)}
                    >
                        <Printer className="size-4 mr-1" />
                        {m.tea_purchase_print_multi()}
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        disabled={exportExcel.isPending}
                        onClick={() => exportExcel.mutate(params)}
                    >
                        <FileDown className="size-4 mr-1" />
                        {m.tea_purchase_export_excel()}
                    </Button>
                    {canWrite && (
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setBulkOpen(true)}
                        >
                            <Rows3 className="size-4 mr-1" />
                            {m.dtp_bulk_open()}
                        </Button>
                    )}
                    {canWrite && (
                        <Button
                            size="sm"
                            onClick={() => setFormState({ open: true })}
                        >
                            <Plus className="size-4 mr-1" />
                            {m.dtp_create()}
                        </Button>
                    )}
                </div>
            </div>

            <ListFilterBar
                {...barProps}
                onRefresh={() => void refetch()}
                refreshing={isFetching}
            />

            {/* Mobile: card list */}
            <div className="space-y-2 md:hidden">
                {isLoading ? (
                    <div className="py-8 text-center text-muted-foreground">
                        {m.common_loading()}
                    </div>
                ) : items.length === 0 ? (
                    emptyState
                ) : (
                    items.map((n) => (
                        <ListCard
                            key={n.id}
                            title={
                                <span className="flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        className="size-4 shrink-0 align-middle"
                                        checked={selectedIds.has(n.id)}
                                        onChange={() => toggleId(n.id)}
                                        aria-label={n.code}
                                    />
                                    <Link
                                        to="/dried-tea-purchases/$id"
                                        params={{ id: n.id }}
                                        className={LIST_CARD_TITLE_CLASS}
                                    >
                                        {n.code}
                                    </Link>
                                </span>
                            }
                            subtitle={n.grower_name || '—'}
                            badge={
                                (n.sources ?? []).length > 0 ? (
                                    <Badge variant="outline">
                                        {m.dtp_sources_count({
                                            count: String(
                                                (n.sources ?? []).length,
                                            ),
                                        })}
                                    </Badge>
                                ) : (
                                    <Badge variant="secondary">
                                        {m.dtp_sources_none()}
                                    </Badge>
                                )
                            }
                            actions={
                                <>
                                    {canWrite ? (
                                        <Tooltip content={m.dtp_edit()}>
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                aria-label={m.dtp_edit()}
                                                onClick={() =>
                                                    setFormState({
                                                        open: true,
                                                        dried: n,
                                                    })
                                                }
                                            >
                                                <Pencil className="size-4" />
                                            </Button>
                                        </Tooltip>
                                    ) : null}
                                    {(n.sources ?? []).length > 0 ? (
                                        <Tooltip
                                            content={m.tea_purchase_print_fresh()}
                                        >
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                aria-label={m.tea_purchase_print_fresh()}
                                                onClick={() =>
                                                    setFreshPrintTarget(n)
                                                }
                                            >
                                                <Printer className="size-4" />
                                            </Button>
                                        </Tooltip>
                                    ) : null}
                                    <Tooltip
                                        content={m.tea_purchase_print_finished()}
                                    >
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            aria-label={m.tea_purchase_print_finished()}
                                            onClick={() => setPrintTarget(n)}
                                        >
                                            <Printer className="size-4" />
                                        </Button>
                                    </Tooltip>
                                    {canWrite ? (
                                        <DriedTeaPurchaseDeleteButton row={n} />
                                    ) : null}
                                </>
                            }
                            metric={{
                                label: m.dtp_quantity(),
                                value: formatQty(n.quantity),
                            }}
                            fields={[
                                {
                                    label: m.dtp_remaining(),
                                    value: formatQty(n.remaining_dry_kg ?? 0),
                                },
                                {
                                    label: m.dtp_grade(),
                                    value: n.tea_grade
                                        ? gradeLabel[n.tea_grade]()
                                        : '—',
                                },
                                {
                                    label: m.tea_purchase_purchase_date(),
                                    value: formatVN(n.purchase_date),
                                    full: true,
                                },
                                {
                                    label: m.tea_purchase_ballot_col(),
                                    value: <BallotCell row={n} />,
                                },
                            ]}
                        />
                    ))
                )}
            </div>

            {/* Desktop: table */}
            <div className="hidden md:block rounded-md border overflow-x-auto">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead className="w-8" />
                            <TableHead>{m.dtp_code()}</TableHead>
                            <TableHead>
                                {m.tea_purchase_filter_grower()}
                            </TableHead>
                            <TableHead>
                                {m.tea_purchase_purchase_date()}
                            </TableHead>
                            <TableHead className="text-right">
                                {m.dtp_quantity()}
                            </TableHead>
                            <TableHead>{m.dtp_grade()}</TableHead>
                            {/* Ba tiêu đề dài được xuống dòng để bảng vừa
                                màn 1440px, cột thao tác không bị đẩy ra
                                ngoài (M-81 thêm cột "Đã trả phiếu"). */}
                            <TableHead className="whitespace-normal">
                                {m.dtp_sources()}
                            </TableHead>
                            <TableHead className="text-right whitespace-normal">
                                {m.dtp_remaining()}
                            </TableHead>
                            <TableHead className="text-center whitespace-normal">
                                {m.tea_purchase_ballot_col()}
                            </TableHead>
                            <TableHead className="text-right">
                                {m.common_actions()}
                            </TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {isLoading ? (
                            <TableRow>
                                <TableCell
                                    colSpan={10}
                                    className="text-center text-muted-foreground"
                                >
                                    {m.common_loading()}
                                </TableCell>
                            </TableRow>
                        ) : items.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={10}>{emptyState}</TableCell>
                            </TableRow>
                        ) : (
                            items.map((n) => (
                                <TableRow key={n.id}>
                                    <TableCell>
                                        <input
                                            type="checkbox"
                                            className="size-4 align-middle"
                                            checked={selectedIds.has(n.id)}
                                            onChange={() => toggleId(n.id)}
                                            aria-label={n.code}
                                        />
                                    </TableCell>
                                    <TableCell className="font-mono text-sm">
                                        <Link
                                            to="/dried-tea-purchases/$id"
                                            params={{ id: n.id }}
                                            className="text-primary hover:underline"
                                        >
                                            {n.code}
                                        </Link>
                                    </TableCell>
                                    <TableCell>
                                        {n.grower_name || '—'}
                                    </TableCell>
                                    <TableCell className="text-xs text-muted-foreground">
                                        {formatVN(n.purchase_date)}
                                    </TableCell>
                                    <TableCell className="text-right tabular-nums">
                                        {formatQty(n.quantity)}
                                    </TableCell>
                                    <TableCell>
                                        {n.tea_grade
                                            ? gradeLabel[n.tea_grade]()
                                            : '—'}
                                    </TableCell>
                                    <TableCell>
                                        {(n.sources ?? []).length > 0 ? (
                                            <Badge variant="outline">
                                                {m.dtp_sources_count({
                                                    count: String(
                                                        (n.sources ?? [])
                                                            .length,
                                                    ),
                                                })}
                                            </Badge>
                                        ) : (
                                            <Badge variant="secondary">
                                                {m.dtp_sources_none()}
                                            </Badge>
                                        )}
                                    </TableCell>
                                    <TableCell className="text-right tabular-nums">
                                        {formatQty(n.remaining_dry_kg ?? 0)}
                                    </TableCell>
                                    <TableCell className="text-center">
                                        <BallotCell row={n} />
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <div className="flex items-center justify-end gap-1">
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                asChild
                                            >
                                                <Link
                                                    to="/dried-tea-purchases/$id"
                                                    params={{ id: n.id }}
                                                >
                                                    <Eye className="size-4" />
                                                </Link>
                                            </Button>
                                            {canWrite && (
                                                <Tooltip content={m.dtp_edit()}>
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        aria-label={m.dtp_edit()}
                                                        onClick={() =>
                                                            setFormState({
                                                                open: true,
                                                                dried: n,
                                                            })
                                                        }
                                                    >
                                                        <Pencil className="size-4" />
                                                    </Button>
                                                </Tooltip>
                                            )}
                                            {(n.sources ?? []).length > 0 && (
                                                <Tooltip
                                                    content={m.tea_purchase_print_fresh()}
                                                >
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        aria-label={m.tea_purchase_print_fresh()}
                                                        onClick={() =>
                                                            setFreshPrintTarget(
                                                                n,
                                                            )
                                                        }
                                                    >
                                                        <Printer className="size-4" />
                                                    </Button>
                                                </Tooltip>
                                            )}
                                            <Tooltip
                                                content={m.tea_purchase_print_finished()}
                                            >
                                                <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    aria-label={m.tea_purchase_print_finished()}
                                                    onClick={() =>
                                                        setPrintTarget(n)
                                                    }
                                                >
                                                    <Printer className="size-4" />
                                                </Button>
                                            </Tooltip>
                                            {canWrite && (
                                                <DriedTeaPurchaseDeleteButton
                                                    row={n}
                                                />
                                            )}
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>

            <div className="flex items-center justify-between">
                <Button
                    variant="outline"
                    size="sm"
                    disabled={page === 1}
                    onClick={() => setPage(page - 1)}
                >
                    {m.common_prev_page()}
                </Button>
                <div className="text-sm text-muted-foreground">
                    {m.common_page()} {page}
                </div>
                <Button
                    variant="outline"
                    size="sm"
                    disabled={page * 50 >= total}
                    onClick={() => setPage(page + 1)}
                >
                    {m.common_next_page()}
                </Button>
            </div>

            {canWrite && (
                <DriedTeaPurchaseBulkDialog
                    open={bulkOpen}
                    onOpenChange={setBulkOpen}
                />
            )}
            <DriedTeaPurchaseFormDialog
                open={formState.open}
                onOpenChange={(v) =>
                    setFormState(
                        v ? { ...formState, open: true } : { open: false },
                    )
                }
                dried={formState.dried}
            />
            <DriedTeaPurchasePrintDialog
                open={!!printTarget}
                onOpenChange={(v) => !v && setPrintTarget(null)}
                dried={printTarget}
            />
            <DriedTeaPurchasePrintDialog
                open={multiPrintOpen}
                onOpenChange={setMultiPrintOpen}
                mode="multi"
                dried={null}
                drieds={selectedRows}
            />
            <DriedFreshSlipsPrintDialog
                open={!!freshPrintTarget}
                onOpenChange={(v) => !v && setFreshPrintTarget(null)}
                dried={freshPrintTarget}
            />
        </div>
    )
}

/**
 * Nút xoá 1 phiếu cân khô. Phiếu đã là nguồn của PNK lô phân loại thì nút mờ đi,
 * hover hiện mã lô để nhân viên biết vì sao không xoá được.
 */
function DriedTeaPurchaseDeleteButton({ row }: { row: DriedTeaPurchase }) {
    const del = useDeleteDriedTeaPurchase()
    return (
        <RowDeleteButton
            label={m.dtp_action_delete()}
            blockedReason={driedTeaPurchaseDeleteBlockReason(row)}
            confirmMessage={m.dtp_delete_confirm({ code: row.code })}
            pending={del.isPending}
            onDelete={() => {
                del.mutate(row.id)
            }}
        />
    )
}
